import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Button,
  Space,
  Tooltip,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  createMenuDiscount,
  updateMenuDiscount,
  deleteMenuDiscount,
  getMenuItems,
} from "../actions/merchant_actions";
import { getAllRewards } from "../actions/acc_actions";
import { useTranslation } from "react-i18next";

const MenuDiscounts = ({ data, refetchMerchant }) => {
  const { t } = useTranslation();
  const accUrn = useSelector((state) => state.account.urn);
  const businessUrn = useSelector((state) => state.account.businessUrn);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [skuOptions, setSkuOptions] = useState([]);
  const [rewardOptions, setRewardOptions] = useState([]);
  const [form] = Form.useForm();

  const { data: itemsData } = useQuery({
    queryKey: ["menuItems", accUrn],
    queryFn: () => getMenuItems({ account_urn: accUrn }),
    enabled: !!accUrn,
  });

  const { data: rewardsData, isLoading } = useQuery({
    queryKey: ["rewards", businessUrn],
    queryFn: () => getAllRewards(businessUrn),
    enabled: !!businessUrn,
  });

  useEffect(() => {
    if (itemsData) {
      const skuOpts = itemsData.map((item) => ({
        label: `${item.data.title} (${item.data.sku})`,
        value: item.data.sku,
      }));
      setSkuOptions(skuOpts);
    }
  }, [itemsData]);

  useEffect(() => {
    if (rewardsData) {
      const rewardOpts = rewardsData?.body?.map((reward) => ({
        label: reward.name,
        value: reward.slug,
      }));
      setRewardOptions(rewardOpts);
    }
  }, [rewardsData]);

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      skus: record.skus,
      reward_type_slug: record.reward_type_slug,
    });
    setIsModalOpen(true);
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const payload = {
        skus: values.skus.join(","),
        reward_type_slug: values.reward_type_slug,
      };

      const action = editingRecord
        ? updateMenuDiscount({
            account_urn: accUrn,
            urn: editingRecord.urn,
            data: payload,
          })
        : createMenuDiscount({
            account_urn: accUrn,
            data: [payload],
          });

      action
        .then(() => {
          message.success(
            editingRecord ? t("discount_updated") : t("discount_created")
          );
          refetchMerchant();
          setIsModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        })
        .catch(() => {
          message.error(
            editingRecord
              ? t("failed_update_discount")
              : t("failed_create_discount")
          );
        });
    });
  };

  const handleDelete = (record) => {
    deleteMenuDiscount({
      account_urn: accUrn,
      urn: record.urn,
    })
      .then(() => {
        message.success(t("discount_deleted"));
        refetchMerchant();
      })
      .catch(() => {
        message.error(t("failed_delete_discount"));
      });
  };

  const columns = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      render: (id) => (
        <Tooltip title={id}>
          <span
            style={{
              maxWidth: 180,
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {id}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t("mode"),
      dataIndex: "mode",
      key: "mode",
      render: (mode) => <Tag color="blue">{mode}</Tag>,
    },
    {
      title: t("skus"),
      dataIndex: "skus",
      key: "skus",
      render: (skus) =>
        skus.map((sku) => (
          <Tag key={sku} color="purple">
            {sku}
          </Tag>
        )),
    },
    {
      title: t("type"),
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag color="green">{type}</Tag>,
    },
    {
      title: t("percent"),
      dataIndex: "percent",
      key: "percent",
      render: (percent) => `${percent * 100}%`,
    },
    { title: t("selection"), dataIndex: "selection", key: "selection" },
    { title: t("applies_to"), dataIndex: "applies_to", key: "applies_to" },
    {
      title: t("combine_with"),
      dataIndex: "combine_with",
      key: "combine_with",
    },
    {
      title: t("reward_slug"),
      dataIndex: "reward_type_slug",
      key: "reward_type_slug",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEditModal(record)}>
            {t("update")}
          </Button>
          <Popconfirm
            title={t("delete_discount_confirm")}
            onConfirm={() => handleDelete(record)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="link" danger>
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "1rem" }}>
      <Space
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          {t("add_discount")}
        </Button>
      </Space>

      <Table
        rowKey={(record) => record.urn || record.id}
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
        scroll={{ x: "max-content" }}
        size="middle"
      />

      <Modal
        title={editingRecord ? t("update_discount") : t("create_discount")}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        okText={editingRecord ? t("update") : t("create")}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t("sku")}
            name="skus"
            rules={[{ required: true, message: t("select_at_least_one_sku") }]}
          >
            <Select
              mode="multiple"
              placeholder={t("select_products_by_sku")}
              options={skuOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label={t("reward_type")}
            name="reward_type_slug"
            rules={[{ required: true, message: t("select_a_reward") }]}
          >
            <Select
              placeholder={t("select_a_reward_type")}
              options={rewardOptions}
              loading={isLoading}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuDiscounts;
